/**
 * Copyright 2026 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';

const imageCache = {};
const failedImageLoadKeys = {};
const MAX_CONCURRENT_IMAGE_LOADS = 1;
const pendingImageLoads = [];
let activeImageLoads = 0;

function finishImageLoad(img, hasError) {
    if (img.__qwcImageLoadFinished) {
        return;
    }

    img.__qwcImageLoadFinished = true;
    img.__qwcImageLoadComplete = !hasError;
    img.__qwcImageLoadError = hasError;
    activeImageLoads = Math.max(0, activeImageLoads - 1);
    if (hasError) {
        markImageLoadFailed(img.__qwcImageSrc, img.__qwcImageFailureKey);
    }
    processImageLoadQueue();
}

function showFallbackImage(img) {
    if (!img || !img.__qwcFallbackSrc) {
        return;
    }

    img.__qwcImageIsFallback = true;
    img.className = img.__qwcFallbackClassName || img.className || "";
    img.alt = img.__qwcFallbackAlt || "";
    img.title = img.__qwcFallbackTitle || "";
    img.onload = null;
    img.onmouseout = null;
    img.onmouseover = null;
    img.ontouchstart = null;
    img.src = img.__qwcFallbackSrc;
}

function markImageLoadFailed(src, failureKey = null) {
    if (!src) {
        return;
    }

    const failedKey = failureKey || src;
    failedImageLoadKeys[failedKey] = true;
    const entry = getCacheEntry(src);
    entry.failed = true;
    entry.available = [];
    Object.keys(imageCache).forEach(cacheSrc => {
        const cacheEntry = imageCache[cacheSrc];
        cacheEntry.available = cacheEntry.available.filter(img => img.__qwcImageFailureKey !== failedKey);
        cacheEntry.used.forEach(img => {
            if (img.__qwcImageSrc === src || img.__qwcImageFailureKey === failedKey) {
                showFallbackImage(img);
            }
        });
    });
    for (let i = pendingImageLoads.length - 1; i >= 0; --i) {
        if (pendingImageLoads[i].__qwcImageSrc === src || pendingImageLoads[i].__qwcImageFailureKey === failedKey) {
            pendingImageLoads[i].__qwcImageLoadQueued = false;
            pendingImageLoads.splice(i, 1);
        }
    }
}

function isImageLoadFailed(src, failureKey = null) {
    const entry = getCacheEntry(src);
    return entry.failed || failedImageLoadKeys[failureKey || src] === true;
}

function processImageLoadQueue() {
    while (activeImageLoads < MAX_CONCURRENT_IMAGE_LOADS && pendingImageLoads.length > 0) {
        const img = pendingImageLoads.shift();
        img.__qwcImageLoadQueued = false;

        if (!img.__qwcImageSrc || img.__qwcImageLoadStarted || img.__qwcImageLoadComplete || isImageLoadFailed(img.__qwcImageSrc, img.__qwcImageFailureKey)) {
            continue;
        }

        activeImageLoads += 1;
        img.__qwcImageLoadStarted = true;
        img.src = img.__qwcImageSrc;

        setTimeout(() => {
            if (img.__qwcImageLoadStarted && !img.__qwcImageLoadFinished && img.complete) {
                finishImageLoad(img, img.naturalWidth === 0);
            }
        }, 0);
    }
}

function queueImageLoad(img) {
    if (!img || !img.__qwcImageSrc || img.__qwcImageLoadQueued || img.__qwcImageLoadStarted || img.__qwcImageLoadComplete || isImageLoadFailed(img.__qwcImageSrc, img.__qwcImageFailureKey)) {
        return;
    }

    img.__qwcImageLoadQueued = true;
    pendingImageLoads.push(img);
    processImageLoadQueue();
}

function getCacheEntry(src) {
    if (!imageCache[src]) {
        imageCache[src] = {
            available: [],
            failed: false,
            used: []
        };
    }
    return imageCache[src];
}

function setStyle(img, style) {
    img.style.cssText = "";
    Object.entries(style || {}).forEach(([key, value]) => {
        img.style[key] = typeof value === "number" ? value + "px" : value;
    });
}

function resetImage(img) {
    img.className = "";
    img.alt = "";
    img.title = "";
    img.onload = null;
    img.onmouseout = null;
    img.onmouseover = null;
    img.ontouchstart = null;
    img.__qwcFallbackAlt = null;
    img.__qwcFallbackClassName = null;
    img.__qwcFallbackSrc = null;
    img.__qwcFallbackTitle = null;
    img.__qwcImageIsFallback = false;
    setStyle(img, null);
}

function configureImage(img, props, fallback = false) {
    img.__qwcFallbackAlt = props.fallbackAlt || props.alt || "";
    img.__qwcFallbackClassName = props.fallbackClassName || props.className || "";
    img.__qwcFallbackSrc = props.fallbackSrc || null;
    img.__qwcFallbackTitle = props.fallbackTitle || props.title || "";

    img.className = fallback ? img.__qwcFallbackClassName : (props.className || "");
    img.alt = fallback ? img.__qwcFallbackAlt : (props.alt || "");
    img.title = fallback ? img.__qwcFallbackTitle : (props.title || "");
    img.onload = fallback ? null : (props.onLoad || null);
    img.onmouseout = fallback ? null : (props.onMouseOut || null);
    img.onmouseover = fallback ? null : (props.onMouseOver || null);
    img.ontouchstart = fallback ? null : (props.onTouchStart || null);
    setStyle(img, props.style);
}

function createImage(src, failureKey = null) {
    const img = new Image();
    img.__qwcImageSrc = src;
    img.__qwcImageFailureKey = failureKey || src;
    img.__qwcImageLoadError = false;
    img.__qwcImageLoadComplete = false;
    img.__qwcImageLoadFinished = false;
    img.__qwcImageLoadQueued = false;
    img.__qwcImageLoadStarted = false;
    img.addEventListener("load", () => finishImageLoad(img, false));
    img.addEventListener("error", () => finishImageLoad(img, true));
    img.decoding = "async";
    queueImageLoad(img);
    return img;
}

function createFallbackImage(src) {
    const img = new Image();
    img.__qwcImageIsFallback = true;
    img.decoding = "async";
    img.src = src;
    return img;
}

export function preloadCachedImage(src, failureKey = null) {
    if (!src) {
        return;
    }
    const entry = getCacheEntry(src);
    if (isImageLoadFailed(src, failureKey)) {
        return;
    }
    const existingImage = [...entry.available, ...entry.used].find(img => !img.__qwcImageLoadError);
    if (!existingImage) {
        entry.available.push(createImage(src, failureKey));
    }
}

export function isCachedImageLoadFailed(src, failureKey = null) {
    if (!src) {
        return false;
    }
    return isImageLoadFailed(src, failureKey);
}

export default class CachedImage extends React.Component {
    static propTypes = {
        alt: PropTypes.string,
        cacheKey: PropTypes.string,
        className: PropTypes.string,
        fallbackAlt: PropTypes.string,
        fallbackClassName: PropTypes.string,
        fallbackSrc: PropTypes.string,
        fallbackTitle: PropTypes.string,
        onLoad: PropTypes.func,
        onMouseOut: PropTypes.func,
        onMouseOver: PropTypes.func,
        onTouchStart: PropTypes.func,
        src: PropTypes.string.isRequired,
        style: PropTypes.object,
        title: PropTypes.string
    }
    componentDidMount() {
        this.attachImage();
    }
    componentDidUpdate(prevProps) {
        if (prevProps.src !== this.props.src) {
            this.releaseImage();
            this.attachImage();
        } else if (this.img) {
            configureImage(this.img, this.props, this.img.__qwcImageIsFallback === true);
        }
    }
    componentWillUnmount() {
        this.releaseImage();
    }
    attachImage = () => {
        if (!this.el || !this.props.src) {
            return;
        }

        const entry = getCacheEntry(this.props.src);
        if (isImageLoadFailed(this.props.src, this.props.cacheKey)) {
            if (this.props.fallbackSrc) {
                this.img = createFallbackImage(this.props.fallbackSrc);
                this.imgSrc = null;
                configureImage(this.img, this.props, true);
                this.el.appendChild(this.img);
            }
            return;
        }

        this.img = entry.available.pop();
        while (this.img && this.img.__qwcImageLoadError) {
            this.img = entry.available.pop();
        }
        this.img = this.img || createImage(this.props.src, this.props.cacheKey);
        queueImageLoad(this.img);
        this.imgSrc = this.props.src;
        entry.used.push(this.img);
        configureImage(this.img, this.props);
        this.el.appendChild(this.img);

        if (this.img.__qwcImageLoadComplete && this.props.onLoad) {
            setTimeout(() => {
                if (this.img) {
                    this.props.onLoad({target: this.img});
                }
            }, 0);
        }
    }
    releaseImage = () => {
        if (!this.img) {
            return;
        }

        if (this.img.parentNode) {
            this.img.parentNode.removeChild(this.img);
        }
        if (!this.imgSrc) {
            resetImage(this.img);
            this.img = null;
            return;
        }

        const entry = getCacheEntry(this.imgSrc);
        entry.used = entry.used.filter(img => img !== this.img);
        resetImage(this.img);
        if (!entry.failed) {
            entry.available.push(this.img);
        }
        this.img = null;
        this.imgSrc = null;
    }
    render() {
        return <span ref={el => { this.el = el; }} />;
    }
}
